const lang = window.appLocale;

/**
 * Calculates the total quantity of products that have a discount promotion.
 *
 * @param {Array} items - The list of items to check for discount promotions.
 * @returns {number} - The total quantity of products with a discount promotion.
 */
export const findTotalQuantityProductsDiscountPromoted = (items) => {
    let quantity = 0
    for (const item of items) {
        if(item.attributes.promotion && item.attributes.promotion.type === 'discount'){
            quantity += item.quantity
        }
    }
    return quantity
}

/**
 * Given a promotion and a quantity purchased, return the applicable discount percentage from the given quantity.
 *
 * This method iterates through the discounts in descending order of quantity, and returns the discount percentage for the
 * first discount that is applicable for the given quantity purchased. If no discount is applicable, it returns null.
 *
 * @param {Object} promotion - The promotion to check for a discount percentage.
 * @param {number} quantity - The quantity purchased of the product to which the promotion is applied.
 * @returns {number|null} The discount percentage applicable for the given quantity purchased, or null if no discount is applicable.
 */
export const findDiscountByQuantity = (promotion, quantity) => {
  if (promotion?.type !== 'discount') return false;

  const discounts = promotion?.discounts
  let discount = null

  for (const [key, value] of Object.entries(discounts)) {
    if(quantity >= key) discount = value
  }


  return discount
}

/**
 * Validates the basic requirements for a given promotion.
 *
 * @param {Object} promotion - The promotion to validate.
 * @param {Object} product - The product to which the promotion is applied.
 * @param {number} quantityPurchased - The quantity of the product purchased.
 * @returns {Object|null} The valid promotion or null if the promotion is not applicable.
 */
export const validBasicRequirement = (promotion, product, quantityPurchased) => {
    if(promotion?.type === 'discount') return null;

    switch (promotion?.requirements) {
        case 'none':
            return promotion;
        case 'quantity':
            return validateQuantity(promotion, quantityPurchased);
        case 'monto':
            return validateMonto(promotion, product, quantityPurchased);
        default:
            return null;
    }
}

/**
 * Validates the quantity requirement for a given promotion.
 *
 * Given a promotion and a quantity purchased, return the promotion if the promotion is valid and applicable for the given quantity purchased.
 * The quantity purchased must be greater than or equal to the required quantity for the promotion to be applicable.
 *
 * @param {Object} promotion - The promotion to validate.
 * @param {number} quantityPurchased - The quantity of the product purchased.
 * @returns {Object|null} The valid promotion or null if the promotion is not applicable.
 */
export const validateQuantity = (promotion, quantityPurchased) => {
    if(promotion.type === 'product' && promotion.product_discount != 100) return quantityPurchased > promotion.req_qty ? promotion : null;

    return quantityPurchased >= promotion.req_qty ? promotion : null;
}

/**
 * Validates the monto requirement for a given promotion.
 *
 * Given a promotion, a product, and a quantity purchased, return the promotion if the promotion is valid and applicable for the given quantity purchased.
 * The total price of the product purchased must be greater than or equal to the required quantity for the promotion to be applicable.
 *
 * @param {Object} promotion - The promotion to validate.
 * @param {Object} product - The product to which the promotion is applied.
 * @param {number} quantityPurchased - The quantity of the product purchased.
 * @returns {Object|null} The valid promotion or null if the promotion is not applicable.
 */
export const validateMonto = (promotion, product, quantityPurchased) => {
    return quantityPurchased * product.price >= promotion.req_qty ? promotion : null;
}


/**
 * Determines if a promotion item should be shown in the cart.
 *
 * A promotion item should be shown if the promotion is applicable for the given quantity purchased.
 *
 * @param {Object} item - The item to check for a promotion.
 * @param {number} quantityProductsPromoted - The total quantity of products with a discount promotion.
 * @returns {boolean} - True if the promotion item should be shown, false otherwise.
 */
export const showPromotionItem = (item, quantityProductsPromoted) => {
    const validPromotion = validBasicRequirement(item.attributes.promotion, item, item.quantity);
    if(validPromotion) return true;

    return findDiscountByQuantity(item.attributes.promotion, quantityProductsPromoted);
}

/**
 * Returns the description for a given promotion item in the cart.
 *
 * The description is applicable only if the promotion is valid and applicable for the given quantity purchased.
 * If the promotion is of type 'product', the description is in the format of "X products a Y$ each".
 * If the promotion is of type 'discount', the description is the description provided in the promotion itself.
 *
 * @param {Object} item - The item to get the description for.
 * @param {number} quantityProductsPromoted - The total quantity of products with a discount promotion.
 * @returns {string} - The description of the promotion, or an empty string if the promotion is not applicable.
 */
export const promotionDescription = (item, quantityProductsPromoted) => {
    if(!showPromotionItem(item, quantityProductsPromoted)) return '';

    return item.attributes.promotion?.type === 'product' ? productPromotionDescription(item, numProductsDiscounted(item.attributes.promotion, item.quantity), item.attributes.promotion.product_discount) : item.attributes.promotion?.description[lang]
}

/**
 * Returns a string description of a product promotion in the format of "X products at Y% off".
 *
 * @param {number} units - The number of units of the product in the promotion.
 * @param {number} discount - The discount percentage in the promotion.
 * @returns {string} - The description of the promotion.
 */
// const productPromotionDescription = (item, units, discount) => {
//     if (discount === 100) {
//         return (
//             units +
//             'x ' +
//             item.name[lang] + ' ' +item.attributes.description
//         );
//     }

//     return (
//         units +
//         ' ' +
//         (units > 1 ? (lang == 'es' ? 'unidades' : 'units') : (lang == 'es' ? 'unidad' : 'unit')) +
//         ' ' +
//         (lang == 'es' ? 'al ' : 'at ') +
//         discount +
//         '%' +
//         (lang == 'es' ? '' : ' off')
//     );
// };
const productPromotionDescription = (item, units, discount) => {
    const unitsText = units > 1
        ? (lang === 'es' ? 'unidades' : lang === 'fr' ? 'unités' : lang === 'de' ? 'Einheiten' : 'units')
        : (lang === 'es' ? 'unidad' : lang === 'fr' ? 'unité' : lang === 'de' ? 'Einheit' : 'unit');

    const atText = lang === 'es' ? 'al ' : lang === 'fr' ? 'à ' : lang === 'de' ? 'bei ' : 'at ';

    if (discount === 100) {
        return `${units}x ${item.name[lang]} ${item.attributes.description?.[lang] || ''}`;
    }

    return `${units} ${unitsText} ${atText}${discount}%${lang === 'es' ? '' : lang === 'fr' ? '' : lang === 'de' ? '' : ' off'}`;
};



/**
 * Returns the promotion information for a given item in the cart.
 *
 * The promotion information is applicable only if the promotion is valid and applicable for the given quantity purchased.
 * If the promotion is of type 'basic', it returns 0.
 * If the promotion is of type 'discount', it returns the discount percentage prefixed with a minus sign.
 * If no promotion is applicable, it returns an empty string.
 *
 * @param {Object} item - The item to get the promotion information for.
 * @param {number} quantityProductsPromoted - The total quantity of products with a discount promotion.
 * @returns {string|number} - The promotion information, or an empty string if no promotion is applicable.
 */
export const promotionInfo = (item, quantityProductsPromoted) => {
    if(!showPromotionItem(item, quantityProductsPromoted)) return '';

    switch (item.attributes.promotion?.type) {
        case 'basic':
            return 0;
        case 'discount':
            return '- ' + findDiscountByQuantity(item.attributes.promotion, quantityProductsPromoted) + '%';
        case 'product':
            if(item.attributes.promotion?.product_discount != 100) return '';
            return lang == 'es' ? 'Gratis' : 'Free';
        default:
            return '';
    }
}


/**
 * Calculates the number of products that are eligible for a discount based on the promotion's required quantity.
 *
 * Given a promotion and the quantity purchased, this function determines how many products can be discounted.
 * The calculation is done by dividing the quantity purchased by the required quantity plus one, and then flooring the result.
 *
 * @param {Object} promotion - The promotion which contains the required quantity for a discount.
 * @param {number} quantityPurchased - The total quantity of the product purchased.
 * @returns {number} - The number of products eligible for a discount.
 */
export const numProductsDiscounted = (promotion, quantityPurchased) => {
    let requiredQuantity = parseInt(promotion?.req_qty);
    if (isNaN(requiredQuantity) || requiredQuantity <= 0) return 0;

    if(promotion.type === 'product' && promotion.product_discount != 100) requiredQuantity++;

    let products = Math.floor(quantityPurchased / (requiredQuantity));

    return products;
}
